'use client';

import Link from 'next/link';
import { useState } from 'react';
import { INDUSTRIES } from '@/lib/industries';
import { GROWTH_PLAYS } from '@/lib/growth-loops';
import { IndustryIcon } from './industry-icon';
import { NavIcon } from './nav-icon';

/** Short names for the picker chips (same as the header menu). */
const SHORT: Record<string, string> = {
  hvac: 'HVAC',
  construction: 'Construction',
  solar: 'Solar',
  'home-services': 'Home services',
  legal: 'Law firms',
  medical: 'Clinics',
  franchise: 'Franchises',
  'multi-location': 'Multi-location',
  'healthcare-systems': 'Hospitals',
  'service-businesses': 'Other services',
};

/** Pick a trade, see the freebie and the referral play we would set up for it. */
export function GrowthLoopsPicker({ initial = 'hvac' }: { initial?: string }) {
  const list = INDUSTRIES.filter((i) => GROWTH_PLAYS[i.slug]);
  const [slug, setSlug] = useState(initial);
  const ind = list.find((i) => i.slug === slug) ?? list[0];
  const p = GROWTH_PLAYS[ind.slug];

  return (
    <div>
      <p className="text-sm font-semibold text-mist-500">Pick your business</p>
      <div role="tablist" aria-label="Industry" className="-mx-4 mt-3 flex gap-2 overflow-x-auto px-4 pb-2 sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0">
        {list.map((i) => {
          const on = i.slug === ind.slug;
          return (
            <button
              key={i.slug}
              type="button"
              role="tab"
              aria-selected={on}
              onClick={(e) => {
                setSlug(i.slug);
                e.currentTarget.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
              }}
              className={`press flex min-h-[2.75rem] shrink-0 items-center gap-2 rounded-full border px-4 text-sm font-semibold transition-colors ${on ? 'border-[#09090b] bg-[#09090b] text-white' : 'border-line-2 bg-ink-900 text-mist-300 hover:border-mist-500'}`}
            >
              <IndustryIcon slug={i.slug} className={`h-4 w-4 ${on ? 'text-signal' : 'text-spark-600'}`} />
              {SHORT[i.slug] ?? i.label}
            </button>
          );
        })}
      </div>

      <div key={ind.slug} role="tabpanel" className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Freebie */}
        <div className="step-in-fwd flex flex-col rounded-[24px] border border-line-2 bg-ink-900 p-6 sm:p-7">
          <span className="flex items-center gap-3 text-sm font-semibold text-mist-500">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-spark-500/10 text-spark-600">
              <NavIcon name="book" className="h-5 w-5" />
            </span>
            The freebie
          </span>
          <h3 className="mt-5 font-display text-2xl font-bold leading-tight tracking-[-0.03em] text-mist-100">{p.freebie.title}</h3>
          <p className="mt-3 leading-relaxed text-mist-400">{p.freebie.body}</p>
          <p className="mt-auto border-t border-line pt-4 text-sm text-mist-300">
            A visitor who is not ready to call leaves an email. The guide arrives, and you follow up.
          </p>
        </div>

        {/* Referral */}
        <div className="step-in-fwd flex flex-col rounded-[24px] bg-[#09090b] p-6 text-white sm:p-7" style={{ animationDelay: '80ms' }}>
          <span className="flex items-center gap-3 text-sm font-semibold text-white/60">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-signal text-on-signal">
              <NavIcon name="handshake" className="h-5 w-5" />
            </span>
            The referral program
          </span>
          <h3 className="mt-5 font-display text-2xl font-bold leading-tight tracking-[-0.03em]">{p.referral.who}</h3>
          <p className="mt-3 leading-relaxed text-white/70">{p.referral.body}</p>
          <p className="mt-auto border-t border-white/15 pt-4 text-sm">
            <span className="font-semibold text-signal">Reward: </span>
            {p.referral.reward}
          </p>
        </div>

        {/* Why it makes them the default */}
        <div className="step-in-fwd rounded-[24px] bg-signal p-6 text-on-signal sm:p-7 lg:col-span-2" style={{ animationDelay: '160ms' }}>
          <p className="text-sm font-semibold opacity-75">Why you become the default choice</p>
          <p className="mt-2 font-display text-[clamp(1.35rem,2.6vw,1.9rem)] font-bold leading-snug tracking-[-0.025em]">{p.default}</p>
          {p.caution && <p className="mt-4 border-t border-on-signal/20 pt-3 text-sm">{p.caution}</p>}
        </div>
      </div>

      <Link href={ind.href} className="mt-5 inline-flex min-h-[2.75rem] items-center gap-2 text-sm font-semibold text-spark-600 underline-offset-4 hover:underline">
        Everything we do for {ind.slug === 'hvac' ? 'HVAC' : (SHORT[ind.slug] ?? ind.label).toLowerCase()} →
      </Link>
    </div>
  );
}
